<template>
  <vx-card :title="t('nav_quotations')">
    <!-- Filtre par statut : un seul menu « Devis » pour tout (Chady, 2026-11-03). -->
    <template slot="actions">
      <div class="flex flex-wrap">
        <vs-button v-for="f in filters" :key="f.key" size="small" class="ml-2 mb-1"
          :type="filter === f.key ? 'filled' : 'border'" :color="f.color" @click="filter = f.key">
          {{ f.label }} ({{ count(f.key) }})
        </vs-button>
      </div>
    </template>

    <div v-if="loading" class="cp-state">
      <feather-icon icon="LoaderIcon" svgClasses="h-6 w-6" class="cp-spin" />
      <div>{{ t('loading') }}</div>
    </div>
    <div v-else-if="loadError" class="cp-state">
      <vs-alert color="danger" active="true">{{ loadError }}</vs-alert>
      <vs-button size="small" class="mt-4" @click="load">{{ t('retry') }}</vs-button>
    </div>
    <div v-else-if="!shown.length" class="cp-state">
      <feather-icon icon="InboxIcon" svgClasses="h-8 w-8" />
      <div>{{ t('no_quotations') }}</div>
    </div>

    <vs-table v-else :data="shown" search pagination :max-items="20" @selected="row => open('client-order', row.id)">
      <template slot="thead">
        <vs-th sort-key="id">{{ t('quotation') }}</vs-th>
        <vs-th sort-key="status">{{ t('status') }}</vs-th>
        <vs-th sort-key="date">{{ t('date') }}</vs-th>
        <vs-th sort-key="ref_client">{{ t('your_ref') }}</vs-th>
        <vs-th v-if="manyCompanies" sort-key="company">{{ t('company') }}</vs-th>
        <vs-th sort-key="date_delivery_desired">{{ t('requested_delivery') }}</vs-th>
        <vs-th>{{ t('transport') }}</vs-th>
        <vs-th>{{ t('invoices_of_order') }}</vs-th>
        <vs-th>{{ t('total_excl') }}</vs-th>
        <vs-th>{{ t('total_incl') }}</vs-th>
        <vs-th></vs-th>
      </template>

      <template slot-scope="{data}">
        <vs-tr v-for="o in data" :key="o.id" :data="o" class="cp-row-link">
          <vs-td :data="o.id"><span class="font-semibold">#{{ o.id }}</span></vs-td>
          <vs-td :data="o.status">
            <vs-chip :color="o.awaiting_confirmation ? '#bcbcbc' : statusColor(o.status)">{{ t('status_' + o.status) }}</vs-chip>
            <vs-chip v-if="o.availability === 'in_stock'" color="success">{{ t('in_stock') }}</vs-chip>
            <vs-chip v-else-if="o.availability === 'to_prepare'" color="danger">{{ t('to_prepare') }}</vs-chip>
          </vs-td>
          <vs-td :data="o.date">{{ date(o.date_approved || o.date) }}</vs-td>
          <vs-td :data="o.ref_client">{{ o.ref_client || '—' }}</vs-td>
          <vs-td v-if="manyCompanies" :data="o.company">{{ o.company }}</vs-td>
          <vs-td :data="o.date_delivery_desired">{{ date(o.date_delivery_desired) || '—' }}</vs-td>
          <vs-td>
            <template v-if="o.transport && o.transport.loading_date">
              <div class="font-semibold whitespace-no-wrap">{{ date(o.transport.loading_date) }}</div>
              <div class="cp-muted text-sm whitespace-no-wrap">#{{ o.transport.id }}</div>
            </template>
            <span v-else class="cp-muted">—</span>
          </vs-td>
          <vs-td>
            <span v-for="inv in o.invoices" :key="inv.id" class="mr-2 whitespace-no-wrap">{{ inv.invoice_no }}</span>
            <span v-if="!o.invoices.length" class="cp-muted">—</span>
          </vs-td>
          <vs-td class="cp-num">{{ amount(o.totals.ht, o.totals.currency) }}</vs-td>
          <vs-td class="cp-num">{{ amount(o.totals.ttc, o.totals.currency) }}</vs-td>
          <vs-td>
            <!-- Comme la colonne Actions des listes de l'ERP (CellRendererActions) : œil = fiche, fichier = PDF. -->
            <div class="flex items-center">
              <feather-icon icon="EyeIcon" svgClasses="h-5 w-5 mr-4 hover:text-primary cursor-pointer" :title="t('details')" @click.stop="open('client-order', o.id)" />
              <feather-icon v-if="o.has_pdf" icon="FileTextIcon" title="PDF" :class="{ 'opacity-50 pointer-events-none': opening['order-' + o.id] }"
                svgClasses="h-5 w-5 hover:text-primary cursor-pointer" @click.stop="viewPdf('order', o.id)" />
            </div>
          </vs-td>
        </vs-tr>
      </template>
    </vs-table>
  </vx-card>
</template>

<script>
// Portail client → « Devis » : tous ses devis et commandes (en attente, en cours,
// facturés), filtrables par statut, sans limite ; clic → fiche (ClientDocument).
// Les commandes en cours sont aussi sur le tableau de bord.
import portalPage from '@/views/client/portalPage.js'

export default {
  name: 'ClientOrders',
  mixins: [portalPage],
  data () {
    return { orders: [], filter: 'all' }
  },
  computed: {
    filters () {
      return [
        { key: 'all', label: this.t('all_statuses'), color: 'dark' },
        { key: 'pending', label: this.t('status_pending'), color: 'warning' },
        { key: 'in_progress', label: this.t('status_in_progress'), color: 'primary' },
        { key: 'invoiced', label: this.t('status_invoiced'), color: 'success' }
      ]
    },
    shown () {
      return this.filter === 'all' ? this.orders : this.orders.filter(o => o.status === this.filter)
    }
  },
  methods: {
    count (key) {
      return key === 'all' ? this.orders.length : this.orders.filter(o => o.status === key).length
    },
    load () {
      return this.fetch('/orders/all', (data) => { this.orders = data.orders || [] })
    }
  },
  created () {
    // Lien direct vers un statut (tableau de bord, anciennes adresses) : ?status=pending|in_progress|invoiced
    const st = this.$route.query.status
    if (['pending', 'in_progress', 'invoiced'].includes(st)) this.filter = st
    this.load()
  }
}
</script>
